import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { GoogleOAuthProvider } from '@react-oauth/google';
import * as Sentry from '@sentry/react';
import { initAnalytics } from './lib/analytics';
import App from './App.tsx';
import './index.css';

const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

// Initialize Sentry before React renders
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.DEV ? 'development' : 'production',
    enabled: !import.meta.env.DEV,
    sendDefaultPii: false,
  });
}

// Initialize PostHog analytics (disabled in development)
initAnalytics();

// PRO-331: typography preview hook. Reads ?fs=<px> from the URL on initial
// load and applies it as html { font-size }. Used by /preview-typography to
// render side-by-side iframes at different root sizes for visual comparison.
// No effect when the param is absent.
const fsParam = new URLSearchParams(window.location.search).get('fs');
if (fsParam) {
  const px = parseFloat(fsParam);
  if (!isNaN(px) && px >= 12 && px <= 24) {
    document.documentElement.style.fontSize = `${px}px`;
  }
}

const root = createRoot(document.getElementById('root')!);

if (import.meta.env.DEV) {
  root.render(
    <StrictMode>
      <GoogleOAuthProvider clientId={googleClientId}>
        <App />
      </GoogleOAuthProvider>
    </StrictMode>
  );
} else {
  root.render(
    <GoogleOAuthProvider clientId={googleClientId}>
      <App />
    </GoogleOAuthProvider>
  );
}

// Register service worker. Used for PWA installability AND web push.
// Registered in dev too so push subscription can be tested locally; if
// dev caching causes issues, unregister via Application > Service Workers
// in DevTools.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('/sw.js').catch(() => {});
}

// Capture beforeinstallprompt globally for PWA install banner
import { setDeferredPrompt } from './lib/pwaInstall';
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  setDeferredPrompt(e as any);
});
